import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  Button,
  Divider,
  Chip,
  LinearProgress,
  Alert,
  Badge,
  Skeleton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { api } from "../utils/api";

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(25,118,210,0.12), transparent 60%)," +
      "radial-gradient(1200px 600px at 90% 10%, rgba(156,39,176,0.12), transparent 60%)," +
      "linear-gradient(180deg, rgba(0,0,0,0.02), transparent 20%)",
  },
  shell: { maxWidth: 1280, margin: "0 auto" },
  glass: {
    borderRadius: 18,
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 14px 40px rgba(0,0,0,0.08)",
    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(8px)",
  },
  liftCard: {
    borderRadius: 18,
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
    transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 18px 50px rgba(0,0,0,0.10)",
      borderColor: "rgba(25,118,210,0.25)",
    },
  },
  fadeIn: {
    animation: "fadeUp 520ms ease both",
  },
};

function toCenterId(centerField) {
  if (!centerField) return null;
  if (typeof centerField === "string") return centerField;
  if (typeof centerField === "object") return centerField._id || centerField.id || null;
  return null;
}

function safeArr(x) {
  return Array.isArray(x) ? x : [];
}

function parseISODate(d) {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function daysBetween(a, b) {
  // b - a in days
  const ms = b.getTime() - a.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

const StatTile = ({ title, value, icon, hint }) => {
  return (
    <Card sx={{ ...styles.liftCard }}>

      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack spacing={0.6}>
            <Typography sx={{ fontSize: 12.5, opacity: 0.7 }}>{title}</Typography>
            <Typography sx={{ fontSize: 34, fontWeight: 1000, lineHeight: 1 }}>
              {value}
            </Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.6 }}>{hint}</Typography>
          </Stack>
          <Box sx={{ opacity: 0.85 }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const CenterCard = ({ center, counts, alertsCount, onOpen }) => {
  const total = (counts.doctors || 0) + (counts.employees || 0) + (counts.vendors || 0);

  return (

    <Card sx={{ ...styles.liftCard, overflow: "hidden" }}>
      <Box
        sx={{
          height: 5,
          background:
            alertsCount > 0
              ? "linear-gradient(90deg, rgba(255,152,0,0.9), rgba(244,67,54,0.9))"
              : "linear-gradient(90deg, rgba(25,118,210,0.9), rgba(156,39,176,0.9))",
        }}
      />
      <CardContent sx={{ p: 7.9 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box>
            <Typography sx={{ fontWeight: 1000, fontSize: 24 }}>{center?.name || "Center"}</Typography>
            <Typography sx={{ opacity: 0.7, fontSize: 12.5 }}>
              {center?.code ? `Code: ${center.code}` : "—"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Badge
              badgeContent={alertsCount}
              color={alertsCount > 0 ? "warning" : "primary"}
              overlap="circular"
            >
              <ApartmentIcon />
            </Badge>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Chip size="small" icon={<MedicalServicesIcon />} label={`Doctors: ${counts.doctors || 0}`} />
          <Chip size="small" icon={<PeopleAltIcon />} label={`Employees: ${counts.employees || 0}`} />
          <Chip size="small" icon={<LocalShippingIcon />} label={`Vendors: ${counts.vendors || 0}`} />
        </Stack>

        <Box sx={{ mt: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 12, opacity: 0.65 }}>Activity</Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.65 }}>{total} total</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, total * 10)}
            sx={{ mt: 0.8, height: 8, borderRadius: 999, opacity: 0.9 }}
          />
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={onOpen}
            sx={{ borderRadius: 3, fontWeight: 900 }}
          >
            View Center
          </Button>

          <Tooltip title={alertsCount > 0 ? "There are contract alerts in this center" : "All good"}>
            <Box
              sx={{
                width: 46,
                display: "grid",
                placeItems: "center",
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "rgba(255,255,255,0.7)",
              }}
            >
              {alertsCount > 0 ? (
                <WarningAmberIcon sx={{ opacity: 0.85 }} />
              ) : (
                <CheckCircleIcon sx={{ opacity: 0.7 }} />
              )}
            </Box>
          </Tooltip>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default function ProDashboard() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [centers, setCenters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [vendors, setVendors] = useState([]);

  const now = useMemo(() => new Date(), []);
  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
    []
  );

  const load = async () => {
    try {
      setErr("");
      setLoading(true);

      const [cRes, eRes, dRes, vRes] = await Promise.all([
        api.get("/centers"),
        api.get("/employees"),
        api.get("/doctors"),
        api.get("/vendors"),
      ]);

      setCenters(safeArr(cRes.data?.data));
      setEmployees(safeArr(eRes.data?.data));
      setDoctors(safeArr(dRes.data?.data));
      setVendors(safeArr(vRes.data?.data));
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const countsByCenter = new Map();

    const ensure = (centerId) => {
      if (!centerId) return null;
      if (!countsByCenter.has(centerId)) {
        countsByCenter.set(centerId, { doctors: 0, employees: 0, vendors: 0 });
      }
      return countsByCenter.get(centerId);
    };

    doctors.forEach((x) => {
      const cid = toCenterId(x.center);
      const slot = ensure(cid);
      if (slot) slot.doctors += 1;
    });

    employees.forEach((x) => {
      const cid = toCenterId(x.center);
      const slot = ensure(cid);
      if (slot) slot.employees += 1;
    });

    vendors.forEach((x) => {
      const cid = toCenterId(x.center);
      const slot = ensure(cid);
      if (slot) slot.vendors += 1;
    });

    return countsByCenter;
  }, [doctors, employees, vendors]);

  const contractAlerts = useMemo(() => {
    // alerts from vendors.contract.endDate
    const expired = [];
    const expiringSoon = [];
    const byCenter = new Map();

    const addCenterAlert = (centerId) => {
      if (!centerId) return;
      byCenter.set(centerId, (byCenter.get(centerId) || 0) + 1);
    };

    vendors.forEach((v) => {
      const end = parseISODate(v?.contract?.endDate);
      if (!end) return;

      const cid = toCenterId(v.center);
      const diff = daysBetween(now, end); // end - now in days

      if (diff < 0) {
        expired.push(v);
        addCenterAlert(cid);
      } else if (diff <= 7) {
        expiringSoon.push(v);
        addCenterAlert(cid);
      }
    });

    // sort by endDate ascending
    const sortByEnd = (a, b) => {
      const da = parseISODate(a?.contract?.endDate)?.getTime() || 0;
      const db = parseISODate(b?.contract?.endDate)?.getTime() || 0;
      return da - db;
    };

    expired.sort(sortByEnd);
    expiringSoon.sort(sortByEnd);

    return { expired, expiringSoon, byCenter };
  }, [vendors, now]);

  const totals = useMemo(() => {
    return {
      centers: centers.length,
      employees: employees.length,
      doctors: doctors.length,
      vendors: vendors.length,
      alerts: contractAlerts.expired.length + contractAlerts.expiringSoon.length,
    };
  }, [centers, employees, doctors, vendors, contractAlerts]);

  const recentAlerts = useMemo(() => {
    // show up to 8 alerts: expired first then expiring
    const items = [
      ...contractAlerts.expired.map((v) => ({ v, kind: "EXPIRED" })),
      ...contractAlerts.expiringSoon.map((v) => ({ v, kind: "SOON" })),
    ];
    return items.slice(0, 8);
  }, [contractAlerts]);

  return (
    <Box sx={styles.page}>
      <Box sx={{ height: 88 }} />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <Box sx={{ ...styles.shell, p: 2 }}>
        {/* HERO */}
        <Card sx={{ ...styles.glass, ...styles.fadeIn }}>
          <CardContent sx={{ p: 2.4 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: 24, fontWeight: 1000 }}>
                    HR Dashboard
                  </Typography>
                  <Chip size="small" label={todayLabel} sx={{ opacity: 0.9 }} />
                  {totals.alerts > 0 ? (
                    <Chip
                      size="small"
                      color="warning"
                      icon={<WarningAmberIcon />}
                      label={`${totals.alerts} Contract Alerts`}
                      sx={{ fontWeight: 900 }}
                    />
                  ) : (
                    <Chip size="small" color="success" icon={<CheckCircleIcon />} label="All Contracts OK" />
                  )}
                </Stack>

                <Typography sx={{ opacity: 0.7, mt: 0.8, maxWidth: 760 }}>
                  Centers overview, staff distribution, and contract expiry alerts — clean and actionable.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={load}
                  disabled={loading}
                  sx={{ borderRadius: 3, fontWeight: 900 }}
                >
                  Refresh
                </Button>

                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => (window.location.href = "/centers")}
                  sx={{ borderRadius: 3, fontWeight: 900 }}
                >
                  Manage Centers
                </Button>
              </Stack>
            </Stack>

            {err ? (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 3 }}>
                {err}
              </Alert>
            ) : null}
          </CardContent>
        </Card>

        <Box sx={{ height: 14 }} />

        {/* TOP STATS */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ ...styles.fadeIn, animationDelay: "40ms" }}>
              <StatTile
                title="Centers"
                value={loading ? <Skeleton width={60} /> : totals.centers}
                hint="Total centers"
                icon={<ApartmentIcon sx={{ fontSize: 58 }} />}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ ...styles.fadeIn, animationDelay: "90ms" }}>
              <StatTile
                title="Employees"
                value={loading ? <Skeleton width={60} /> : totals.employees}
                hint="All staff"
                icon={<PeopleAltIcon sx={{ fontSize: 38 }} />}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ ...styles.fadeIn, animationDelay: "140ms" }}>
              <StatTile
                title="Doctors"
                value={loading ? <Skeleton width={60} /> : totals.doctors}
                hint="Medical team"
                icon={<MedicalServicesIcon sx={{ fontSize: 38 }} />}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ ...styles.fadeIn, animationDelay: "190ms" }}>
              <StatTile
                title="Vendors"
                value={loading ? <Skeleton width={60} /> : totals.vendors}
                hint="External vendors"
                icon={<LocalShippingIcon sx={{ fontSize: 38 }} />}
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, opacity: 0.6 }} />

        {/* ALERTS + QUICK ACTIONS */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card sx={{ ...styles.liftCard }}>
              <CardContent sx={{ p: 2.2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>
                    Contract Alerts
                  </Typography>
                  <Chip
                    size="small"
                    color={totals.alerts > 0 ? "warning" : "success"}
                    label={totals.alerts > 0 ? `${totals.alerts} Issues` : "No Issues"}
                    sx={{ fontWeight: 900 }}
                  />
                </Stack>

                <Typography sx={{ opacity: 0.7, fontSize: 13, mt: 0.7 }}>
                  Expired contracts + contracts expiring within 7 days.
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  {loading ? (
                    <Stack spacing={1}>
                      <Skeleton height={22} />
                      <Skeleton height={22} />
                      <Skeleton height={22} />
                    </Stack>
                  ) : totals.alerts === 0 ? (
                    <Alert severity="success" sx={{ borderRadius: 3 }}>
                      All vendor contracts are valid.
                    </Alert>
                  ) : (
                    <>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <Chip
                          size="small"
                          color="error"
                          label={`Expired: ${contractAlerts.expired.length}`}
                        />
                        <Chip
                          size="small"
                          color="warning"
                          label={`Expiring (7d): ${contractAlerts.expiringSoon.length}`}
                        />
                      </Stack>

                      <List dense sx={{ p: 0 }}>
                        {recentAlerts.map(({ v, kind }) => {
                          const end = v?.contract?.endDate ? String(v.contract.endDate).slice(0, 10) : "—";
                          const centerName =
                            (typeof v.center === "object" && v.center?.name) ||
                            centers.find((c) => c._id === toCenterId(v.center))?.name ||
                            "—";
                          return (
                            <ListItem key={v._id} sx={{ px: 0 }}>
                              <ListItemText
                                primary={
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                      size="small"
                                      color={kind === "EXPIRED" ? "error" : "warning"}
                                      icon={<WarningAmberIcon />}
                                      label={kind === "EXPIRED" ? "Expired" : "Expiring Soon"}
                                      sx={{ fontWeight: 900 }}
                                    />
                                    <Typography sx={{ fontWeight: 900, fontSize: 13.5 }}>
                                      {v.name}
                                    </Typography>
                                  </Stack>
                                }
                                secondary={
                                  <Typography sx={{ opacity: 0.75, fontSize: 12 }}>
                                    Center: {centerName} • End: {end}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>

                      <Button
                        variant="outlined"
                        sx={{ mt: 1.2, borderRadius: 3, fontWeight: 900 }}
                        onClick={() => (window.location.href = "/vendors")}
                      >
                        Review Vendors
                      </Button>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ ...styles.liftCard }}>
              <CardContent sx={{ p: 2.2 }}>
                <Typography sx={{ fontWeight: 1000, fontSize: 16 }}>
                  Quick Actions
                </Typography>
                <Typography sx={{ opacity: 0.7, fontSize: 13, mt: 0.7 }}>
                  Jump to the most used modules.
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                  <Button sx={{ borderRadius: 3, fontWeight: 900 }} variant="contained" onClick={() => (window.location.href = "/centers")}>
                    Centers
                  </Button>
                  <Button sx={{ borderRadius: 3, fontWeight: 900 }} variant="outlined" onClick={() => (window.location.href = "/employees")}>
                    Employees
                  </Button>
                  <Button sx={{ borderRadius: 3, fontWeight: 900 }} variant="outlined" onClick={() => (window.location.href = "/doctors")}>
                    Doctors
                  </Button>
                  <Button sx={{ borderRadius: 3, fontWeight: 900 }} variant="outlined" onClick={() => (window.location.href = "/vendors")}>
                    Vendors
                  </Button>
                </Stack>

                <Divider sx={{ my: 2, opacity: 0.6 }} />

                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
                  Center Health
                </Typography>
                <Typography sx={{ opacity: 0.7, fontSize: 12.5, mt: 0.5 }}>
                  Alerts are calculated from vendor contract end dates.
                </Typography>

                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ opacity: 0.7, fontSize: 12 }}>Alerts coverage</Typography>
                    <Typography sx={{ opacity: 0.7, fontSize: 12 }}>
                      {totals.alerts === 0 ? "100%" : `${Math.max(0, 100 - totals.alerts * 5)}%`}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={totals.alerts === 0 ? 100 : Math.max(10, 100 - totals.alerts * 5)}
                    sx={{ mt: 0.8, height: 9, borderRadius: 999 }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, opacity: 0.6 }} />

        {/* CENTERS GRID */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>
            Centers
          </Typography>
          <Typography sx={{ opacity: 0.7, fontSize: 13 }}>
            Each card shows staff + vendors + alerts
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ ...styles.liftCard }}>
                  <CardContent>
                    <Skeleton height={20} width="60%" />
                    <Skeleton height={16} width="35%" />
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                      <Skeleton height={28} width={110} />
                      <Skeleton height={28} width={110} />
                      <Skeleton height={28} width={110} />
                    </Stack>
                    <Skeleton height={16} sx={{ mt: 2 }} />
                    <Skeleton height={42} sx={{ mt: 1.5 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))
            : centers.map((c, idx) => {
              const cid = c._id;
              const counts = grouped.get(cid) || { doctors: 0, employees: 0, vendors: 0 };
              const alertsCount = contractAlerts.byCenter.get(cid) || 0;

              return (
                <Grid item xs={12} sm={6} md={4} key={cid}>
                  <Box
                    sx={{
                      ...styles.fadeIn,
                      animationDelay: `${60 + idx * 50}ms`,
                    }}
                  >
                    <CenterCard
                      center={c}
                      counts={counts}
                      alertsCount={alertsCount}
                      onOpen={() => (window.location.href = `/centers/${cid}`)}
                    />
                  </Box>
                </Grid>
              );
            })}
        </Grid>

        <Box sx={{ height: 24 }} />
      </Box>
    </Box>
  );
}
